import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/States";
import MappingReport from "@/components/bankrecon/MappingReport";
import ReconResult from "@/components/bankrecon/ReconResult";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STL = { taslak: "Taslak", kaydedildi: "Kaydedildi", yeniden_acildi: "Yeniden açıldı" };

export default function BankRecon() {
  const { user, can } = useAuth();
  const edit = can("finans", "edit") || can("finans", "mutabakat");
  const [accs, setAccs] = useState(null);
  const [acc, setAcc] = useState("");
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/bank-recon/accounts").then(({ data }) => { setAccs(data); setAcc(data.items[0]?.id || ""); }); }, []);
  const loadHist = () => acc && api.get("/bank-recon/sessions", { params: { account_id: acc } }).then(({ data }) => setHist(data.items));
  useEffect(() => { loadHist(); }, [acc]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (mapping) => {
    if (!file) return toast.error("Ekstre dosyası seçin");
    const fd = new FormData();
    fd.append("account_id", acc); fd.append("file", file); fd.append("remember", "true");
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    setBusy(true);
    try {
      const { data } = await api.post("/bank-recon/preview", fd);
      setDraft(data);
      if (data.already_reconciled) toast.info(`Bu dönemde ${data.already_reconciled} hareket zaten mutabık; tekrar eşleştirilmez`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const open = async (id) => setDraft((await api.get(`/bank-recon/sessions/${id}`)).data);
  const reopen = async (id) => {
    if (!window.confirm("Oturum yeniden açılsın mı? İşaretli hareketlerin mutabakat işareti kaldırılır.")) return;
    try { await api.post(`/bank-recon/sessions/${id}/reopen`); toast.success("Yeniden açıldı"); loadHist(); open(id); } catch (e) { toast.error(apiError(e)); }
  };
  if (!accs) return <div className="h-40 animate-pulse" />;
  return (
    <div className="space-y-4" data-testid="bankrecon-page">
      <PageHeader title="Banka Ekstresi Eşleştir" subtitle="Ekstreyi (Excel/CSV · Ziraat, İş, Garanti, Akbank, YKB veya genel) yükleyin, sistem hareketleriyle eşleştirin, mutabakatı kaydedin" testid="bankrecon-title" />
      <div className="panel p-3 flex flex-wrap items-end gap-3">
        <label className="text-[12px]">Banka hesabı<select className="block h-9 min-w-[240px] rounded border px-2" value={acc} onChange={(e) => { setAcc(e.target.value); setDraft(null); }} data-testid="bankrecon-account">
          {accs.items.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}{a.has_mapping ? " (eşleme kayıtlı)" : ""}</option>)}</select></label>
        {edit && <>
          <input type="file" accept=".xlsx,.csv,.txt" onChange={(e) => setFile(e.target.files[0])} className="text-[13px]" data-testid="bankrecon-file" />
          <Button disabled={busy || !acc} onClick={() => upload()} data-testid="bankrecon-upload"><Upload className="h-4 w-4 mr-1" />{busy ? "Okunuyor…" : "Yükle ve eşleştir"}</Button>
        </>}
      </div>
      {draft && <>
        <div className="text-[13px] text-slate-600" data-testid="bankrecon-draft-info">{draft.file_name} · {draft.period_start} → {draft.period_end} · {draft.lines.length} satır · <b>{STL[draft.status]}</b>{draft.saved_by_name && ` · ${draft.saved_by_name}`}</div>
        {draft.status !== "kaydedildi" && <MappingReport key={draft.id} draft={draft} fields={accs.fields} onRemap={upload} />}
        <ReconResult key={`${draft.id}-${draft.status}`} draft={draft} onSaved={() => { loadHist(); open(draft.id); }} />
      </>}
      <div className="panel overflow-hidden" data-testid="bankrecon-history">
        <div className="px-4 py-2 border-b font-semibold text-sm">Mutabakat Geçmişi</div>
        <table className="erp-table"><thead><tr><th>Dönem</th><th>Dosya</th><th>Durum</th><th>Özet</th><th>Kaydeden</th><th /></tr></thead>
          <tbody>{hist.map((h) => (
            <tr key={h.id} data-testid={`bankrecon-session-${h.id}`}>
              <td className="font-mono text-xs">{h.period_start} → {h.period_end}</td><td className="text-xs">{h.file_name}</td><td>{STL[h.status]}</td>
              <td className="text-xs">{h.summary ? `${h.summary.matched} eşleşme · ${h.summary.unmatched_bank} ekstre · ${h.summary.unmatched_system} sistem` : "—"}</td>
              <td className="text-xs">{h.saved_by_name || h.created_by_name}</td>
              <td className="whitespace-nowrap"><Button size="sm" variant="ghost" onClick={() => open(h.id)} data-testid={`bankrecon-open-${h.id}`}><Eye className="h-4 w-4" /></Button>
                {user?.is_super && h.status === "kaydedildi" && <Button size="sm" variant="ghost" onClick={() => reopen(h.id)} data-testid={`bankrecon-reopen-${h.id}`}><RotateCcw className="h-4 w-4" /></Button>}</td>
            </tr>))}</tbody></table>
      </div>
    </div>
  );
}
